/* useLiveAutomations.js — calm polling hook for the Automations page.
 *
 * Polls the WAE summary + pending runs every 15s when the tab is visible,
 * pauses when hidden. Structured so a future swap to WebSocket/SSE only
 * requires replacing this hook's internals — the public API stays.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const POLL_MS = 15_000;

export function useLiveAutomations({ status = "pending_approval" } = {}) {
  const [summary, setSummary]   = useState(null);
  const [runs, setRuns]         = useState([]);
  const [rules, setRules]       = useState([]);
  const [recent, setRecent]     = useState([]); // last 30 across statuses
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const aliveRef = useRef(true);

  const refetch = useCallback(async () => {
    try {
      const [sRes, rRes, ruRes, recentRes] = await Promise.all([
        api.get("/api/automations/summary"),
        api.get(`/api/automations/runs?status=${encodeURIComponent(status)}&limit=200`),
        api.get("/api/automations/rules"),
        api.get("/api/automations/runs?status=all&limit=30"),
      ]);
      if (!aliveRef.current) return;
      setSummary(sRes?.data || null);
      setRuns(rRes?.data?.runs || []);
      setRules(ruRes?.data?.rules || []);
      setRecent(recentRes?.data?.runs || []);
      setError(null);
    } catch (e) {
      if (!aliveRef.current) return;
      const code = e?.response?.status;
      setError(code === 403
        ? "Automations is available to admin / director / manager roles only."
        : "Could not load automations.");
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    aliveRef.current = true;
    refetch();
    const tick = () => {
      if (document.visibilityState === "visible") refetch();
    };
    const interval = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      aliveRef.current = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  return { summary, runs, rules, recent, loading, error, refetch };
}
